import { useState } from 'react';
import { useBranding } from '../../lib/branding.tsx';
import { BrandMark, displayBrandName, isVendorBrandName } from '../BrandMark.tsx';

/**
 * The brand at the top of every auth screen — ONE component, because it was
 * five copies of the same three-way branch (see the logoUrl note in
 * branding.tsx for what the copies got wrong once already).
 *
 * - An uploaded logo renders large: 56px tall, capped at 260px wide so a
 *   landscape wordmark-style logo cannot push the form column wider. It was
 *   40px, which read as an afterthought beside a 26px heading.
 * - A tenant WITHOUT a logo gets its own name as the header, NOT the
 *   SupplierClear mark. The mark is the product's identity; beside a tenant's
 *   name it read as the tenant's logo, which it is not.
 * - Only the product's own brand keeps the mark.
 *
 * A logo that fails to load (a deleted object, a bucket that moved with a cell)
 * falls back to the name rather than leaving a broken-image icon at the top of
 * a sign-in page. The failure is keyed on the URL, so a later valid URL — the
 * preload's revalidation, or a Settings upload — is tried afresh.
 */
export function SignInBrandLogo({
  align = 'start',
  className = '',
}: {
  /** `center` for the older centred card screens (expired links). */
  align?: 'start' | 'center';
  /** Layout only — spacing. */
  className?: string;
}) {
  const { branding } = useBranding();
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const brandName = displayBrandName(branding.companyName);
  const logoUrl = branding.logoUrl && branding.logoUrl !== failedUrl ? branding.logoUrl : '';
  const centered = align === 'center';

  let content;
  if (logoUrl) {
    content = (
      <img
        key={logoUrl}
        src={logoUrl}
        alt={brandName}
        onError={() => setFailedUrl(logoUrl)}
        className={`h-14 w-auto max-w-[260px] object-contain ${
          centered ? 'mx-auto object-center' : 'object-left'
        }`}
      />
    );
  } else if (isVendorBrandName(brandName)) {
    content = <BrandMark size={34} />;
  } else {
    content = (
      // Not an <h1>: every screen using this has its own heading, and a page
      // with two <h1>s announces the tenant name as the page title.
      <div
        className="min-w-0 break-words text-[26px] font-bold leading-tight tracking-[-0.5px] text-[var(--ui-text-ink,#0f172a)]"
      >
        {brandName}
      </div>
    );
  }

  return <div className={`${centered ? 'flex justify-center text-center' : ''} ${className}`}>{content}</div>;
}
